// app/src/components/guard/GuardConnectPage.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import PageShell from "../common/PageShell";
import { useAuthState } from "../app/AuthStateContext";
import { resolveAvatarSrc, pickAvatarUpdatedAtMs } from "../../utils/avatarUrl";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

// Build the desktop deep link the browser hands off to the installed client.
function buildDeepLink(code, state) {
  const params = new URLSearchParams({ code, state });
  return `ouroguard://auth/callback?${params.toString()}`;
}

export default function GuardConnectPage() {
  const location = useLocation();
  const { user } = useAuthState();
  const state = new URLSearchParams(location.search).get("state") || "";
  const [phase, setPhase] = useState("loading"); // 'loading' | 'launched' | 'error'
  const [error, setError] = useState(null);
  const ranRef = useRef(false);
  const avatarSrc = resolveAvatarSrc({
    basePath,
    picture: user?.picture,
    userId: user?.userId || user?._id,
    avatarUpdatedAtMs: pickAvatarUpdatedAtMs(user),
    hasCustomAvatar: !!user?.avatar,
  });
  const displayName = user?.nickname || user?.name || user?.email || "your Ouro account";

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    async function mintAndHandoff() {
      if (!state) {
        setError("Missing handoff state. Reopen Ouro Guard and try Sign in again.");
        setPhase("error");
        return;
      }
      try {
        const res = await fetch(`${basePath}/api/v1/guard/desktop-code`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state }),
        });
        const data = await res.json().catch(() => null);
        if (res.ok && data?.ok && data.code) {
          window.location.href = buildDeepLink(data.code, state);
          setPhase("launched");
        } else if (res.status === 401) {
          setError("Please sign in to Ouro first, then return to Ouro Guard and retry.");
          setPhase("error");
        } else {
          setError(data?.message || "Could not start the Ouro Guard handoff.");
          setPhase("error");
        }
      } catch (_) {
        setError("Network error while contacting Ouro. Please try again.");
        setPhase("error");
      }
    }

    mintAndHandoff();
  }, [state]);

  return (
    <PageShell>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          textAlign: "center",
          p: 4,
          maxWidth: 560,
          mx: "auto",
        }}
      >
        {phase === "loading" && <CircularProgress sx={{ mb: 3 }} />}

        <Typography variant="h5" fontWeight={700} gutterBottom>
          {phase === "error" ? "Couldn't connect Ouro Guard" : "Connecting Ouro Guard…"}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {phase === "launched"
            ? "Ouro Guard should now be opening. You can return to the desktop app."
            : "Linking your account to the Ouro Guard desktop app."}
        </Typography>

        {user && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 3,
              px: 2,
              py: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              textAlign: "left",
            }}
          >
            <Avatar src={avatarSrc || undefined} alt={displayName} />
            <Box>
              <Typography variant="body2" color="text.secondary">
                Connecting as
              </Typography>
              <Typography variant="subtitle2" fontWeight={700}>
                {displayName}
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>
            {error}
          </Alert>
        )}

        {phase === "error" && (
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            sx={{ textTransform: "none" }}
          >
            Retry
          </Button>
        )}
      </Box>
    </PageShell>
  );
}
